'use client';

import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { PortableText } from '@portabletext/react';

import { GroqImage } from '@/helpers/Image';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/shadcn/ui/carousel';
import { cn } from '@/lib/utils';
import type { PageRizAffiliateSchema } from '@/sanity/pages/PageRizAffiliateTemplate';
import { measuredRichTextDark, V3_HIGHLIGHT } from './richtext';
import ChevronCta from './ChevronCta';
import Eyebrow from './Eyebrow';
import { EXPO, RevealBlock, RevealRichText, RevealStyles } from './about/reveal';
import { CarouselArrow } from './final/CarouselArrow';
import DriftRow from './final/DriftRow';

import noise from '@/public/fabio-affiliate/noise.jpg';

const ACCENTS = [
  { text: '#FFF9A8', bg: 'rgba(255,249,168,0.15)' },
  { text: '#FFDDE8', bg: 'rgba(255,221,232,0.15)' },
  { text: '#DAFFE1', bg: 'rgba(218,255,225,0.15)' },
  { text: '#FFE1BA', bg: 'rgba(255,225,186,0.15)' },
] as const;

const DOT_OPACITIES = ['opacity-10', 'opacity-40', 'opacity-60', 'opacity-80', 'opacity-100'];

const HEADING_CLASS = [
  V3_HIGHLIGHT,
  'richtext richtext-center w-full lg:w-[min(75.25rem,100%)]',
  'prose-headings:text-white prose-headings:font-geist prose-headings:font-[500] prose-headings:text-[3.75rem] prose-headings:tracking-[-0.1875rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.3rem] lg:[&_h2:last-child]:mt-[0.625rem]',
  '[--hl-tracking:0em] lg:[--hl-tracking:-0.0025em] lg:[--hl-ctr:0px] max-lg:[&_span:has(>.hl-wash)]:[--hl-tex:1.25rem] [--hl-wash:4.125rem] lg:[--hl-wash:6.75rem] [--hl-dy:0.25rem] lg:[--hl-dy:-0.375rem] lg:[--hl-pl:1.0375rem] lg:[--hl-pr:0.183em] [&_span:has(>.hl-wash)]:!text-[#A8E8D4] [&_span:has(>.hl-wash)_span]:!text-[#A8E8D4]',
  'max-lg:[&_.hl-tick-l_span]:!bg-[#228E71] max-lg:[&_.hl-tick-r_span]:!bg-[#228E71] lg:[&_.hl-tick-l_span]:!bg-[#4DC7A3] lg:[&_.hl-tick-r_span]:!bg-[#4DC7A3] lg:[&_.hl-wash]:!bg-[rgba(34,142,113,0.28)]',
  'prose-headings:leading-[1.1] lg:prose-headings:leading-none lg:[&_h2:last-child]:leading-[1.15]',
].join(' ');

type Testimonial = PageRizAffiliateSchema['testimonialsItems'][number];

function TestimonialCard({ t, index, className }: { t: Testimonial; index: number; className?: string }) {
  const accent = ACCENTS[Math.floor(index / 2) % ACCENTS.length];
  const [left, ...rest] = (t?.name ?? '').split('—');
  const right = rest.join('—').trim();

  return (
    <article className={cn('flex flex-col justify-between rounded-[0.25rem] pb-[2rem] gap-[2rem]', className)}>
      <div className="flex flex-col items-start gap-[2rem] p-[3rem]">
        {t?.avatar ? (
          <GroqImage
            src={t.avatar}
            alt={t.name ?? ''}
            quality={100}
            sizes="48px"
            style={{ width: '6rem' }}
            className="object-cover rounded-[0.5rem] !w-[6rem] !h-[6rem]"
          />
        ) : (
          t?.initials && (
            <span
              className="flex justify-center items-center font-geist font-[600] rounded-[0.5rem] w-[6rem] h-[6rem] text-[2.25rem] leading-none"
              style={{ backgroundColor: accent.bg, color: accent.text }}>
              {t.initials}
            </span>
          )
        )}
        {t?.quote?.length > 0 && (
          <div className="richtext richtext-start [text-wrap:wrap] prose-p:font-geist prose-p:font-[400] prose-p:text-white prose-p:text-[2.25rem] prose-p:leading-[1.5]">
            <PortableText
              value={t.quote}
              components={measuredRichTextDark}
            />
          </div>
        )}
      </div>
      <div className="flex justify-between items-end gap-[2rem] whitespace-nowrap px-[3rem] pb-[1rem] text-[2.25rem] leading-none">
        {t?.stars && (
          <span
            className="font-geist font-[400]"
            style={{ color: accent.text }}>
            {t.stars}
          </span>
        )}
        {t?.name && (
          <span className="font-geist font-[600] text-white">
            <span style={{ color: accent.text }}>{left.trim()}</span>
            {right && ` — ${right}`}
          </span>
        )}
      </div>
    </article>
  );
}

function Row({
  items,
  indices,
  direction,
  offset,
}: {
  items: Testimonial[];
  indices: number[];
  direction: 'left' | 'right';
  offset: number;
}) {
  return (
    <div className="relative w-full">
      <DriftRow
        direction={direction}
        offset={{ mobile: offset, desktop: offset }}>
        {indices.map((index) => (
          <TestimonialCard
            key={index}
            t={items[index]}
            index={index}
            className="shrink-0 bg-[#0F1F19] w-[50rem] min-h-[37rem] mr-[3rem]"
          />
        ))}
      </DriftRow>
      <div className="absolute inset-y-[-1rem] left-0 w-[42rem] bg-gradient-to-r from-[#050B09] pointer-events-none" />
      <div className="absolute inset-y-[-1rem] right-0 w-[48.125rem] bg-gradient-to-l from-[#050B09] pointer-events-none" />
    </div>
  );
}

export default function TestimonialsSection({
  data,
}: {
  data: PageRizAffiliateSchema;
}) {
  const items = data?.testimonialsItems ?? [];
  const reduce = useReducedMotion();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    const sync = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };
    sync();
    api.on('select', sync);
    api.on('reInit', sync);
    return () => {
      api.off('select', sync);
      api.off('reInit', sync);
    };
  }, [api]);

  if (
    !data?.testimonialsChip &&
    !data?.testimonialsHeading?.length &&
    !data?.testimonialsRating &&
    !data?.testimonialsRatingText?.length &&
    !items.length
  )
    return null;

  const all = items.map((_, i) => i);
  const row1 = all.filter((i) => i % 2 === 0);
  const row2 = all.filter((i) => i % 2 !== 0);
  const progress = count > 0 ? ((current + 1) / count) * 100 : 0;

  return (
    <section className="relative overflow-hidden bg-[#050B09] pt-[8rem] pb-[8.125rem] lg:pt-[15rem] lg:pb-[17.5rem]">
      <RevealStyles />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.09] mix-blend-exclusion pointer-events-none bg-no-repeat bg-[length:296rem_152.25rem] bg-[position:0_-2.125rem] lg:hidden"
        style={{ backgroundImage: `url(${noise.src})` }}
      />

      <div className="flex relative flex-col items-center w-full">
        <div className="flex flex-col items-center w-[min(49.75rem,calc(100%-4rem))] gap-[3.875rem] lg:w-full lg:gap-[3.35rem]">
          <div className="flex flex-col items-center w-full gap-[2rem] lg:gap-[1rem]">
            {data?.testimonialsChip && (
              <motion.div
                initial={reduce ? false : { opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ opacity: { duration: 0.6, ease: 'easeInOut' }, y: { duration: 0.9, ease: EXPO } }}>
                <Eyebrow
                  text={data.testimonialsChip}
                  tone="dark"
                />
              </motion.div>
            )}
            <RevealRichText
              value={data?.testimonialsHeading}
              components={measuredRichTextDark}
              className={HEADING_CLASS}
            />
          </div>

          {(data?.testimonialsRating || data?.testimonialsRatingText?.length > 0) && (
            <RevealBlock className="flex flex-col items-center gap-[1rem]">
              {data?.testimonialsRating && (
                <div className="flex items-center gap-[1rem]">
                  <p className="m-0 font-geist font-[600] tabular-nums text-white/70 text-[2rem] leading-[1.33]">
                    {data.testimonialsRating}
                  </p>
                  <span
                    aria-hidden
                    className="flex justify-between items-center w-[7.875rem]">
                    {DOT_OPACITIES.map((opacity) => (
                      <span
                        key={opacity}
                        className={cn('bg-white w-[0.75rem] h-[0.75rem] shrink-0', opacity)}
                      />
                    ))}
                  </span>
                </div>
              )}
              {data?.testimonialsRatingText?.length > 0 && (
                <div className="richtext richtext-center w-[min(43.5rem,100%)] [text-wrap:wrap] prose-p:font-geist prose-p:text-white/70 prose-p:text-[2rem] prose-p:leading-[1.33] prose-strong:font-[600] prose-strong:text-white/70">
                  <PortableText
                    value={data.testimonialsRatingText}
                    components={measuredRichTextDark}
                  />
                </div>
              )}
            </RevealBlock>
          )}
        </div>

        {items.length > 0 && (
          <>
            <RevealBlock className="hidden lg:flex flex-col w-full gap-[2.75rem] mt-[4.775rem]">
              <Row
                items={items}
                indices={row1}
                direction="left"
                offset={-78}
              />
              {row2.length > 0 && (
                <Row
                  items={items}
                  indices={row2}
                  direction="right"
                  offset={-100.75}
                />
              )}
            </RevealBlock>

            <RevealBlock className="flex flex-col items-center w-full gap-[3rem] mt-[4.5rem] lg:hidden">
              <Carousel
                setApi={setApi}
                opts={{ loop: true, align: 'center' }}
                className="w-full">
                <CarouselContent>
                  {items.map((t, i) => (
                    <CarouselItem
                      key={i}
                      className="flex basis-auto px-[1rem]">
                      <TestimonialCard
                        t={t}
                        index={i}
                        className="bg-[#112E27] w-[min(49.75rem,calc(100vw-4rem))] min-h-[34.75rem]"
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
              <div className="flex items-center gap-[2.5rem] w-[min(49.75rem,calc(100%-4rem))]">
                <CarouselArrow
                  direction="prev"
                  onClick={() => api?.scrollPrev()}
                  className="bg-white/30 text-[#161616]"
                />
                <div className="flex-1 overflow-hidden h-[0.25rem] bg-[#D6EEE7]/[0.15]">
                  <div
                    className="h-full bg-[#D6EEE7] transition-[width] duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <CarouselArrow
                  direction="next"
                  onClick={() => api?.scrollNext()}
                  className="bg-white text-[#161616]"
                />
              </div>
            </RevealBlock>
          </>
        )}

        {data?.testimonialsCTA && (
          <RevealBlock className="flex justify-center w-[min(49.75rem,calc(100%-4rem))] mt-[3rem] lg:w-auto lg:mt-[9.25rem]">
            <ChevronCta
              data={data.testimonialsCTA}
              variant="white"
              language={data?.settings?.language}
            />
          </RevealBlock>
        )}
      </div>
    </section>
  );
}
